<script lang="ts">
	// C3, choose a shop (UC-31): a button that names the shop shown opens a
	// sheet listing the restaurant's shops, each with its address. Picking one
	// opens its address, which the catalogue's worker renders, so every shop's
	// menu has a URL of its own and the browser fetches no data. Drawn by the
	// worker with the page, then mounted over that markup. On a phone the
	// list rises from the foot, as the item sheet does, and the phone's Back
	// closes it.

	import Button from '../components/Button.svelte';
	import Chip from '../components/Chip.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { leaveTo } from '../components/sheetHistory';
	import { shopHref } from './addresses';
	import { restaurant } from './copy';

	interface Shop {
		id: string;
		name: string;
		address: string;
	}

	interface Props {
		slug: string;
		shops: Shop[];
		current: string;
	}

	let { slug, shops, current }: Props = $props();

	let open = $state(false);

	const shown = $derived(shops.find((s) => s.id === current));
	const first = $derived(shops[0]?.id ?? '');

	// The sheet holds an entry in the history while it is open, so a pick
	// first closes it and leaves from the page's own entry: Back from the
	// shop picked then comes straight back here. A pick with a modifier key
	// opens the link as the browser would.
	function pick(event: MouseEvent, href: string) {
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
			return;
		event.preventDefault();
		open = false;
		leaveTo(href);
	}
</script>

<Button
	testId="catalogue-shops"
	tone="secondary"
	size="sm"
	label={restaurant.shopButton(shown?.name ?? '')}
	trailingIcon="expand"
	onPress={() => (open = true)}
/>

<Sheet
	{open}
	title={restaurant.shops}
	closeLabel={restaurant.close}
	onClose={() => (open = false)}
	placement="auto"
	handle
	history
	testId="catalogue-shops-sheet"
	closeTestId="catalogue-shops-close"
	handleTestId="catalogue-shops-handle"
>
	<ul class="shops" data-scroll>
		{#each shops as shop (shop.id)}
			{@const href = shopHref(slug, shop.id === first ? '' : shop.id)}
			<li>
				<a
					class="shop"
					{href}
					onclick={(event) => pick(event, href)}
					aria-current={shop.id === current ? 'page' : undefined}
					data-row
					data-testid="catalogue-shop-{shop.id}"
				>
					<span class="names">
						<span class="name">{shop.name}</span>
						<span class="address">{shop.address}</span>
					</span>
					{#if shop.id === current}
						<Chip tone="ok" dot text={restaurant.current} />
					{/if}
				</a>
			</li>
		{/each}
	</ul>
</Sheet>

<style>
	.shops {
		margin: 0;
		padding: 0;
		list-style: none;
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.shops li {
		display: flex;
	}

	.shop {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		align-items: center;
		gap: var(--space-5);
		padding: var(--space-6) var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
		color: var(--text-strong);
		text-decoration: none;
	}

	.names {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.name {
		font-size: var(--text-base);
		font-weight: var(--weight-bold);
	}

	.address {
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}
</style>
